import { ContentRoute } from '@project/components/Module/Route/Content';
import { FC, useEffect, useMemo, useState } from 'react';
import { Route } from '@project/components/Module/Route/Body';
import { RouteMenu } from '@project/store/Controller';
import { Alert, Divider, Menu, Spin } from 'antd';
import { CarIcon } from '@assets/icons/route/Car';
import { BikeIcon } from '@assets/icons/route/Bike';
import { WalkIcon } from '@assets/icons/route/Walk';
import { Point as GeoJSONPoint } from 'geojson';
import proj4 from 'proj4';
import { makeRoute } from '@project/components/Module/Route/utils';
import { Geolocation as olGeolocation } from 'ol';
import OlMap from 'ol/Map';
import { RecordCard } from '@project/store/shared/RecordCard';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { onDeleteLayers } from '@utils/deleteLayers';
import Feature from 'ol/Feature';
import { Icon, Style } from 'ol/style';
import image from '@assets/images/map-controls/place.svg';
import VectorSource from 'ol/source/Vector';
import { Vector as VectorLayer } from 'ol/layer';
import { Point } from 'ol/geom';
import { ZINDEX } from '@project/components/Map/constants';

export const CardRoute: FC<{
  map: OlMap;
  card: RecordCard;
  onClose(): void;
}> = ({ map, card, onClose }) => {
  const geoloc = useMemo(() => {
    return new olGeolocation({
      tracking: true,
      trackingOptions: {
        enableHighAccuracy: true,
        maximumAge: 10000,
      },
      projection: map.getView().getProjection(),
    });
  }, []);

  const [route, setRoute] = useState<Route | null>(null);
  const [vehicleType, setVehicleType] = useState<RouteMenu>(RouteMenu.CAR);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<boolean>(false);

  const onRouting = async () => {
    setIsLoading(true);
    setError(false);
    try {
      const userPosition = geoloc.getPosition();

      if (!userPosition) {
        geoloc.once('change', () => onRouting());

        return;
      }

      const objectPosition = [
        ...(card.record.record?.geometry as GeoJSONPoint).coordinates,
      ].reverse();

      if (!userPosition || !objectPosition) return;
      const projectedUserPosition = proj4(
        map.getView().getProjection().getCode(),
        'EPSG:4326',
        userPosition,
      ).reverse();

      const markerStart = new Feature({
        geometry: new Point(userPosition),
      });
      const icon = new Style({
        image: new Icon({
          anchor: [0.5, 40],
          anchorXUnits: 'fraction',
          anchorYUnits: 'pixels',
          src: image,
        }),
      });
      markerStart.setStyle(icon);
      markerStart.set('_id', 'cardRouteStart');
      const markerEnd = new Feature({
        geometry: new Point(
          proj4(
            'EPSG:4326',
            map.getView().getProjection().getCode(),
            (card.record.record?.geometry as GeoJSONPoint).coordinates,
          ),
        ),
      });
      markerEnd.setStyle(icon);
      markerEnd.set('_id', 'cardRouteEnd');

      const vectorSource = new VectorSource({
        features: [markerStart, markerEnd],
      });

      const vector = new VectorLayer({
        source: vectorSource,
      });
      vector.setZIndex(ZINDEX.ROUTE_POINT);
      vector.set('_routeLayers', true);

      map.addLayer(vector);

      const mapRoute = await makeRoute(
        projectedUserPosition.join(','),
        objectPosition.join(','),
        vehicleType,
        map,
      );

      setRoute(mapRoute ?? null);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      setError(true);
    }
  };

  useEffect(() => {
    onRouting();
  }, [vehicleType]);

  useEffect(() => {
    return () => {
      onDeleteLayers('_route');
      onDeleteLayers('_geoMarker');
      onDeleteLayers('_routeLayers');
      onDeleteLayers('_modifyRoute');
    };
  }, []);

  return (
    <div className="route-panel">
      <div className={'card-route-panel'}>
        <ArrowLeftOutlined style={{ fontSize: '24px' }} onClick={onClose} />
        <Menu
          className="route-menu"
          onClick={(e) => setVehicleType(e.key as RouteMenu)}
          mode="horizontal"
          selectedKeys={[vehicleType]}
          style={{ borderBottom: 'none' }}
        >
          <Menu.Item key="car">
            <CarIcon />
          </Menu.Item>
          <Menu.Item key="bike">
            <BikeIcon />
          </Menu.Item>
          <Menu.Item key="foot">
            <WalkIcon />
          </Menu.Item>
        </Menu>
      </div>
      <Divider style={{ margin: '0' }} />
      {isLoading ? (
        <div className="spin-route" style={{ bottom: '10%' }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert
          className={'route-error'}
          title="Ошибка"
          description={'При построении маршрута произошла ошибка'}
          type="error"
          showIcon
        />
      ) : (
        route && <ContentRoute isLoading={isLoading} route={route} />
      )}
    </div>
  );
};
